import { find } from 'lodash';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import Modal from 'react-modal';
import { RuntimeContext } from '../../../../common/RuntimeContext';
import { DEFAULT_IMAGE_URLS } from '../../Constants';
import { getModalStyling } from '../../utils/ModalUtils';
import TextInput from '../formComponents/inputs/TextInput';
import VerbSelect from '../formComponents/inputs/VerbSelect';
import Image from '../Image';
import SearchSelect from '../SearchSelect';
import VerbButton from '../VerbButton';
import VerbHeading from '../VerbHeading';

export default class RecommendationModal extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            note: '',
            users: [],
            error: '',
            success: '',
            selectedRecipientType: 'Coworker',
            selectedRecipientTeamId: ''
        };
    }

    static propTypes = {
        parentId: PropTypes.string,
        parentType: PropTypes.string,
        companyId: PropTypes.string,
        company: PropTypes.object,
        currentUserEmail: PropTypes.string,
        onSend: PropTypes.func,
        isOpen: PropTypes.bool,
        forcedRecommendation: PropTypes.bool,
        onClose: PropTypes.func,
        teamsById: PropTypes.object,
        parentEntity: PropTypes.object,
        isAccountOwner: PropTypes.bool,
        isVerbAdmin: PropTypes.bool,
        loggedInUser: PropTypes.object
    };

    static contextType = RuntimeContext;

    _formOptions = (values) => {
        const { currentUserEmail } = this.props;
        const valuesArray = [];
        if (values) {
            values.forEach((item) => {
                if (currentUserEmail !== item.email) {
                    valuesArray.push({
                        value: item._id,
                        label: `${item.firstName} ${item.lastName}`,
                        email: item.email
                    });
                }
            });
        }
        return valuesArray;
    };

    handleSelect = (value) => {
        this.setState({ users: value });
    };

    onTextChange = (e) => {
        const val = e.target.value;
        this.setState({ note: val, error: '' });
    };

    onSend = () => {
        const { note, users, selectedRecipientType } = this.state;
        const { parentId, parentType, onSend } = this.props;

        if (!note) {
            return this.setState({ error: 'A note must be entered to submit.' });
        }

        if (selectedRecipientType === 'Team' && !this.state.selectedRecipientTeamId) {
            return this.setState({ error: 'A team must be selected to submit.' });
        }

        const recommendationParams = { recipientType: selectedRecipientType };

        if (selectedRecipientType === 'Coworker') {
            recommendationParams.recommendations = users.map((user) => ({
                parentId,
                parentType,
                userProfileId: user.value,
                userEmail: user.email,
                userName: user.label,
                note
            }));
        } else if (selectedRecipientType === 'Team') {
            recommendationParams.recipientTeamId = this.state.selectedRecipientTeamId;
            recommendationParams.recommendations = [{ parentId, parentType, note }];
        }

        return onSend({...recommendationParams, pathsContext: this.context.pathsWeb})
            .then(() => this.setState({ note: '', error: '', users: [], success: 'Recommendations sent!' }))
            .catch((error) => {
                if (error) {
                    this.setState({ error: 'Something went wrong..' });
                }
            })
            .then(() => {
                setTimeout(() => {
                    this.setState({ error: '', success: '' });
                }, 5000);
            });
    };

    onCancelRecommendations = () => {
        const { onClose } = this.props;
        this.setState({
            note: '',
            users: [],
            error: ''
        });
        onClose();
    };

    handleFocus = (event) => {
        event.target.select();
    };

    copyClick = () => {
        this.setState({ success: 'Link copied!' });
    };

    _renderHeading = () => {
        const { parentType, forcedRecommendation, parentEntity } = this.props;
        if (forcedRecommendation) {
            return (
                <VerbHeading
                    className={'recommendation-header'}
                    messageId={'FORCED_RECOMMEND_HEADER'}
                    defaultMessage={`You're on a roll!`}
                    hNumber={2}
                />
            );
        }
        let recommendType = parentType === 'COLLECTION' ? 'collection' : 'activity';
        if (parentType === 'PATHWAY') {
            recommendType = 'pathway';
        }
        return (
            <VerbHeading
                className={'recommendation-header'}
                messageId={'RECOMMEND_ENTITY'}
                defaultMessage={`Recommend this ${recommendType}`}
                valuesToInject={{
                    entityName:
                        parentType === 'COLLECTION' || parentType === 'PATHWAY'
                            ? parentEntity.name
                            : (parentEntity.track || {}).name
                }}
                hNumber={2}
            />
        );
    };

    _handleRecipientTypeSelect = (recipientObject) => {
        const { teamsById = {} } = this.props;
        const stateToUpdate = { selectedRecipientType: recipientObject.label };
        if (recipientObject.label === 'Team' && !this.state.selectedRecipientTeamId) {
            Object.values(teamsById).forEach((team) => {
                if (team.isDefaultTeam) {
                    stateToUpdate.selectedRecipientTeamId = team._id;
                }
            });
        }

        this.setState(stateToUpdate);
    };

    _renderRecipientSelectOption = () => {
        const { companyId, teamsById = {}, loggedInUser = {}, isAccountOwner, isVerbAdmin } = this.props;
        const { selectedRecipientType, selectedRecipientTeamId } = this.state;

        let teamOptions = [];

        if (isAccountOwner || isVerbAdmin) {
            teamOptions = Object.values(teamsById);
        } else {
            Object.values(teamsById).forEach((team) => {
                if (team.teamLeads && team.teamLeads.indexOf(loggedInUser._id) > -1) {
                    teamOptions.push(team);
                }
            });
        }

        if (selectedRecipientType === 'Team') {
            return (
                <VerbSelect
                    value={selectedRecipientTeamId}
                    labelKey={'name'}
                    valueKey={'_id'}
                    onChange={(teamObject) => this.setState({ selectedRecipientTeamId: teamObject._id })}
                    options={teamOptions}
                    className={'bordered'}
                    styleProps={'col-12 no-padding'}
                />
            );
        }
        return (
            <SearchSelect
                formOptions={this._formOptions}
                className={'col-12 bordered'}
                indexName={'Profile'}
                isMulti={true}
                placeholder={'Start typing a name'}
                defaultFilters={`companyId:${companyId}`}
                attributeList={['_id', 'firstName', 'lastName', 'email']}
                handleSelect={this.handleSelect}
                restrictSearchableAttributes={['firstName', 'lastName']}
            />
        );
    };

    render() {
        const {
            isOpen,
            company = { name: 'your company' },
            forcedRecommendation = false,
            isAccountOwner,
            isVerbAdmin,
            parentType,
            teamsById = {},
            loggedInUser = {}
        } = this.props;

        const isTeamLeadOfAnyTeam = !!find(teamsById, ({ teamLeads }) => (teamLeads || []).includes(loggedInUser._id));
        const { note, error, success, selectedRecipientType, users } = this.state;
        let recommendType = parentType === 'COLLECTION' ? 'collection' : 'activity';
        let canTeamAdminInvite = false;
        let selectedRecipientTypeOptions = [{ label: 'Coworker' }, { label: 'Team' }];
        let hasNoTeamLeadOptions = false;
        if (parentType === 'PATHWAY') {
            recommendType = 'pathway';
        }
        if (!Object.keys(teamsById).length) {
            selectedRecipientTypeOptions = [{ label: 'Coworker' }];
        }
        if (!isAccountOwner && !isVerbAdmin) {
            hasNoTeamLeadOptions = Object.values(teamsById).every(
                (team) => team.teamLeads && team.teamLeads.indexOf(loggedInUser._id) === -1
            );

            if (hasNoTeamLeadOptions) {
                selectedRecipientTypeOptions = [{ label: 'Coworker' }];
                canTeamAdminInvite = false;
            } else {
                canTeamAdminInvite = company.enablePermalinkInvites;
            }
        }

        return (
            <Modal
                className={'recommendation-modal pushed'}
                style={getModalStyling({ content: { padding: '0 15px 0 0' } })}
                isOpen={isOpen}
                onRequestClose={this.closeModal}
                contentLabel={'Modal'}
            >
                <div className={'row recommendation-image-expanded-container no-padding'}>
                    <Image
                        src={DEFAULT_IMAGE_URLS.RECOMMENDATION_IMAGE}
                        className={'hidden-xs col-4 recommendation-image-expanded'}
                    />
                    <div className={'modal-container main-panel col-12 col-md-8'}>
                        <div className={'close-container'}>
                            <i className={'fal fa-times'} onClick={this.onCancelRecommendations} />
                        </div>
                        <div className={'row'}>
                            <div className={'col-12'}>{this._renderHeading()}</div>
                        </div>
                        <div className={'col-12 recommendation-field'}>
                            <div className={'row'}>
                                <div className={'recommendation-heading'}>
                                    {forcedRecommendation
                                        ? `Recommend this activity to a coworker at ${company.name}`
                                        : `Share this ${recommendType} with others at ${company.name}. They'll receive a notification on Verb and an email with your recommendation.`}
                                </div>
                            </div>
                            <div className={'row recipient-type-label-container'}>
                                <VerbHeading
                                    messageId={'ADD_RECIPIENT_CAPS'}
                                    defaultMessage={'ADD RECIPIENT'}
                                    className={'recipient-type-label'}
                                    hNumber={6}
                                />
                            </div>
                            <div className={'row recipient-type-select-container'}>
                                <div className={'col-12 no-padding'}>
                                    <VerbSelect
                                        value={selectedRecipientType}
                                        labelKey={'label'}
                                        valueKey={'label'}
                                        onChange={this._handleRecipientTypeSelect}
                                        options={selectedRecipientTypeOptions}
                                        className={'col-5 bordered'}
                                    />
                                </div>
                            </div>
                            <div className={'row'}>{this._renderRecipientSelectOption()}</div>
                        </div>
                        <div className={'col-12 recommendation-field'}>
                            <div className={'row'}>
                                <TextInput
                                    componentClass={'textarea'}
                                    onChange={this.onTextChange}
                                    value={note}
                                    rows={4}
                                    maxLength={'500'}
                                    placeholder={'Personalize this recommendation with a note'}
                                    type={'text'}
                                    className={'col-12 no-padding'}
                                />
                            </div>
                        </div>
                        <div className={'row button-row'}>
                            <div className={'col-12'}>
                                <VerbButton
                                    className={'ms-auto'}
                                    disabled={!users.length && selectedRecipientType === 'Peer'}
                                    onClick={this.onSend}
                                >
                                    SEND
                                </VerbButton>
                                {error ? <span className={'error ms-auto'}>{error}</span> : null}
                                {success ? <span className={'success ms-auto'}>{success}</span> : null}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        );
    }
}
